import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Wrench, RefreshCw, Eye } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const STEPS = [
  {
    n: "1",
    title: "Irrota suihkuletku",
    text: "Kierrä suihkuletku irti hanasta tai suihkupäästä käsin – työkaluja ei tarvita.",
  },
  {
    n: "2",
    title: "Kierrä suodatin väliin",
    text: "Kiinnitä JINSU-suodatin hanan ja letkun väliin. Silikoniset O-renkaat estävät vuodot.",
  },
  {
    n: "3",
    title: "Avaa vesi ja nauti",
    text: "Suodatin on heti käyttövalmis. Puhtaampi vesi virtaa ensimmäisestä suihkusta lähtien.",
  },
];

export default function InstallationSection() {
  const root = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from("[data-install-step]", {
        y: 60,
        opacity: 0,
        duration: 0.8,
        stagger: 0.15,
        ease: "power3.out",
        scrollTrigger: { trigger: "[data-install-steps]", start: "top 78%" },
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={root} data-testid="installation-section" className="py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <p className="eyebrow text-water-deep/80">Asennus</p>
          <h2 className="h-section font-heading mt-5 font-semibold text-foreground" lang="fi">
            Asennus käden käänteessä – ilman työkaluja.
          </h2>
        </div>

        <div data-install-steps className="mt-14 md:mt-20 grid gap-6 md:grid-cols-3">
          {STEPS.map((s) => (
            <div
              key={s.n}
              data-install-step
              className="rounded-[2rem] bg-white p-8 md:p-10 shadow-[0_10px_40px_-18px_rgba(23,68,94,0.2)]"
            >
              <span className="font-heading text-5xl font-semibold text-water">{s.n}</span>
              <h3 className="font-heading mt-5 text-xl md:text-2xl font-semibold text-foreground">{s.title}</h3>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-muted-foreground">{s.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div className="flex items-start gap-5 rounded-[2rem] bg-water-soft p-8">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-water-deep shadow-sm">
              <Eye size={22} strokeWidth={1.8} />
            </span>
            <div>
              <h3 className="font-heading text-lg font-semibold text-foreground">Milloin suodatin vaihdetaan?</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Läpinäkyvä runko näyttää, milloin vaihto on ajankohtaista. Suositeltu käyttöikä on
                noin 30–60 päivää veden laadusta ja käytöstä riippuen.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-5 rounded-[2rem] bg-water-soft p-8">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-water-deep shadow-sm">
              <RefreshCw size={22} strokeWidth={1.8} />
            </span>
            <div>
              <h3 className="font-heading text-lg font-semibold text-foreground">Vitamiinisuodattimen riittoisuus</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Noin 7 200 litraa – eli noin 30 päivää nelihenkisessä taloudessa. Säilyvyys
                avaamattomana 3 vuotta valmistuspäivästä.
              </p>
            </div>
          </div>
        </div>

        <p className="mt-10 flex items-center gap-2.5 text-sm font-semibold text-muted-foreground">
          <Wrench size={16} className="text-water-deep" />
          Sopii kaikkiin Suomessa käytössä oleviin hanoihin ja suihkuihin – asennus ei vaadi työkaluja.
        </p>
      </div>
    </section>
  );
}
